import { PagesConfig } from '@utils/pageConfig/PagesConfigContext';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { RelativePeriod, RelativePeriodLabel } from '@components/comparison-period-selector/Types';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { RetailerClientState } from '@state/reducers/retailer-client/RetailerClientReducer';
import { getSubscriptionForOtherProducts } from '@components/multi-banner-retailer-selector/MultiBannerRetailerSelectorUtils';
import { ProductType } from '@pages/product-analyzer/Product';
import {
  CategoryLeaderboardViewState,
  CLRestoreLocalSavedViewProps,
  ConstructGeneralViewStateParam,
  ExploreTabRestoreLocalSavedViewProps,
  ExploreViewState,
  OpportunityReviewRestoreLocalSavedViewProps,
  OpportunityReviewViewState,
  PARestoreLocalSavedViewProps,
  PerformanceAnalysisViewState,
  ProductsViewState,
  ProductTabRestoreLocalSavedViewProps,
  RestoreLocalSavedViewProps,
  RetailerOverviewRestoreLocalSavedViewProps,
  TradingAssistantRestoreLocalSavedViewProps,
  TradingAssistantViewState,
} from '@models/SavedViews';
import { FilterKeys, PageName, PageType, SelectedFilters } from '@utils/pageConfig/Types';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import { RestoreMultibannerRetailerViewProps } from './UseRestoreMultibannerRetailerClient';
import { RestoreProductMultibannerRetailerViewProps } from './UseRestoreProductMultibannerRetailerClient';
import {
  buildDefaultCustomMonthPeriodWithString,
  buildDefaultCustomWeekPeriodWithString,
  getRestoreSaveViewLatestPeriod,
  isCustomPeriodTimeFrame,
  isCustomWeeks,
  isPeriodicityMATOrYTD,
} from '@utils/PeriodUtils';
import { BaseTimePeriod } from '@services/FiltersTypes';
import { CustomMonthsPeriodState, CustomWeeksPeriodState } from '@state/reducers/main-filter/MainFiltersReducer';
import { InitialFilter } from '@state/reducers/initial-filter/InitialFiltersReducer';
import { SortingOptions } from '@components/design/migrated/Table/Sorting/types';
import { getCarryOverAllFiltersForApply, getExcludedPagesToCarryOver } from '@utils/filterTrayUtils/FilterTrayUtils';
import { byFilterKey } from '@utils/pageConfig/Predicates/SelectedFiltersPredicates';

//Check if the current user's client subscription contains saved views client that user wanted to access
export const checkIsSubscribed = (
  retailerClientsState: RetailerClientState,
  pagesConfig: PagesConfig,
  productType: ProductType,
): boolean => {
  return getSubscriptionForOtherProducts(productType, pagesConfig, retailerClientsState.clientId).length !== 0;
};

const checkIsEmptyMainFilters = (
  timeFrame: Periodicity,
  comparisonPeriod: RelativePeriodLabel | BaseTimePeriod,
  growthType: string,
): boolean => {
  return Object.keys(timeFrame).length === 0 || Object.keys(comparisonPeriod).length === 0 || growthType.length === 0;
};

const getRelativePeriod = (comparisonPeriod: RelativePeriodLabel | BaseTimePeriod) => {
  return comparisonPeriod === RelativePeriodLabel.PreviousPeriod
    ? RelativePeriod.PreviousPeriod
    : RelativePeriod.PreviousYearCurrentPeriod;
};

export const getGrowthType = (growthType: string) => {
  return growthType.toLowerCase() === GrowthTypes.actual ? GrowthTypes.actual : GrowthTypes.percentage;
};

const getInitialFiltersByCountryAndPageType = (
  viewState:
    | PerformanceAnalysisViewState
    | CategoryLeaderboardViewState
    | ProductsViewState
    | OpportunityReviewViewState,
  countryToFind: string[],
  pageType: PageType,
) => {
  // PageType is used in favour of ProductType as there are multiple product types that are allowed
  // to view hitlist data, namely ProductAnalyzer, RetailKad and SellerInsights
  const initialFilters =
    pageType === PageType.RI_PRODUCT
      ? viewState.hitlistInitialFilterState.initialFilters
      : viewState.initialFilterState.initialFilters;

  return initialFilters.filter((initialFilter) => countryToFind.includes(initialFilter.country));
};

// restores the states that are common in all pages. This logic was extracted out to reduce duplicated code
export const restoreGeneralViewState = (
  params: ConstructGeneralViewStateParam,
  setCustomMonthsPeriodState?: (customMonthsPeriodState: CustomMonthsPeriodState) => void,
  setCustomWeeksPeriodState?: (customWeeksPeriodState: CustomWeeksPeriodState) => void,
) => {
  const {
    viewState,
    pagesConfig,
    generalSavedViewProps: {
      setTimeFrame,
      setPeriod,
      setRelativePeriod,
      setSelectedCountries,
      setAppliedCountries,
      setSelectedProductGroupOptions,
      setAppliedProductGroupOptions,
    },
  } = params;

  let latestPeriod = params.mainFiltersState.period;
  let viewStateComparisonPeriod: RelativePeriod | BaseTimePeriod = getRelativePeriod(viewState.comparisonPeriod);

  // Restore applied filters
  if (viewState.appliedFilters) {
    const appliedFilters: SelectedFilters[] = [...pagesConfig.appliedFilters];
    // This is a mutation of the current pagesConfig object
    appliedFilters[params.pageType].selectedFilters = cloneDeep(viewState.appliedFilters);

    const countryOptions =
      viewState.appliedFilters.find((filter) => filter.filterKey === FilterKeys.COUNTRY)?.options ?? [];
    const productGroupOptions =
      viewState.appliedFilters.find((filter) => filter.filterKey === FilterKeys.PRODUCT_GROUP)?.options ?? [];

    setSelectedProductGroupOptions(productGroupOptions);
    setAppliedProductGroupOptions(productGroupOptions);
    setSelectedCountries(countryOptions);
    setAppliedCountries(countryOptions);

    const pagesConfigWithCurrentPageType = { ...pagesConfig, pageType: params.pageType };
    const excludedPagesForCarryOverFilters = getExcludedPagesToCarryOver(pagesConfigWithCurrentPageType);
    const carriedOverAllFilters = getCarryOverAllFiltersForApply(
      appliedFilters,
      params.pageType,
      undefined,
      excludedPagesForCarryOverFilters,
    );

    // We need to set the currency filter here to prevent it from being overwritten as a result of
    // calling pagesConfig.setAppliedFilters(appliedFilters) or pagesConfig.setSelectedFilters(appliedFilters)
    // more than once.
    if (viewState.currencyFilters) {
      carriedOverAllFilters[PageType.PERFORMANCE_ANALYSIS].selectedFilters.push(
        ...viewState.currencyFilters.filter(byFilterKey(FilterKeys.CURRENCY)),
      );
    }

    pagesConfig.setAppliedFilters(carriedOverAllFilters);
    pagesConfig.setSelectedFilters(carriedOverAllFilters);

    // Update Side Filter Redux
    params.updateSideFilters({
      appliedFilters: carriedOverAllFilters,
    });

    const countryList = countryOptions.map((country) => country.value);

    // Restore with latest period
    const initialFilter = getInitialFiltersByCountryAndPageType(viewState, countryList, params.pageType);

    if (initialFilter) {
      latestPeriod = getLatestPeriod(
        initialFilter,
        viewState.periodicity,
        latestPeriod,
        viewState.matYtdTimePeriodLabel,
      );
    }
  }

  //For Custom Month or Custom Week, need to manually construct relative period as its in range format instead of RelativePeriod
  const isCustomPeriodicity = isCustomPeriodTimeFrame(viewState.periodicity);

  // Restore main filters
  if (viewState.periodicity && !isCustomPeriodicity) {
    //special handling to MAT and YTD to avoid the api call is correct when involve timeframe
    const handledPeriodicity = getHandledTimeFrame(viewState.periodicity, viewState.matYtdTimePeriodLabel);
    setTimeFrame(handledPeriodicity);

    pagesConfig.setSelectedTimeFrame(handledPeriodicity);

    // Set latest period
    setPeriod(latestPeriod);
  }

  // Restore comparison period
  if (viewState.comparisonPeriod) {
    setRelativePeriod(getRelativePeriod(viewState.comparisonPeriod));
  }

  // Restore of Custom Periods due to default period and comparison in range format
  if (isCustomPeriodicity) {
    const customMonthPeriod = buildDefaultCustomMonthPeriodWithString(
      latestPeriod.label,
      latestPeriod.lastUpdatedDate,
      0,
    );

    const customMonthComparisonPeriod = buildDefaultCustomMonthPeriodWithString(
      latestPeriod.label,
      latestPeriod.lastUpdatedDate,
      1,
    );

    const customWeekPeriod = buildDefaultCustomWeekPeriodWithString(
      latestPeriod.label,
      latestPeriod.lastUpdatedDate,
      0,
    );

    const customWeekComparisonPeriod = buildDefaultCustomWeekPeriodWithString(
      latestPeriod.label,
      latestPeriod.lastUpdatedDate,
      1,
    );

    setTimeFrame(viewState.periodicity);
    pagesConfig.setSelectedTimeFrame(viewState.periodicity);

    const isCustomWeeksSelected = isCustomWeeks(viewState.periodicity);

    //Set period for custom timeframe
    setPeriod(getValueByCustomPeriodType(isCustomWeeksSelected, customWeekPeriod, customMonthPeriod));

    //Set comparison period for custom timeframe
    setRelativePeriod(
      getValueByCustomPeriodType(isCustomWeeksSelected, customWeekComparisonPeriod, customMonthComparisonPeriod),
    );

    //assign latest period with custom timeframe to update storeMainfilterSetting
    latestPeriod = getValueByCustomPeriodType(isCustomWeeksSelected, customWeekPeriod, customMonthPeriod);
    viewStateComparisonPeriod = getValueByCustomPeriodType(
      isCustomWeeksSelected,
      customWeekComparisonPeriod,
      customMonthComparisonPeriod,
    );

    const customPeriodState = { period: latestPeriod, comparisonPeriod: viewStateComparisonPeriod };

    // Update Redux value for Performance Analysis Saved View
    setCustomPeriod(customPeriodState, isCustomWeeksSelected ? setCustomWeeksPeriodState : setCustomMonthsPeriodState);
  }

  // Update Main Filter Redux
  if (!checkIsEmptyMainFilters(viewState.periodicity, viewState.comparisonPeriod, viewState.growthType)) {
    const storeMainFilterSettings = {
      timeFrame: viewState.periodicity,
      period: latestPeriod,
      comparisonPeriod: viewStateComparisonPeriod,
      growthType: getGrowthType(viewState.growthType),
    };

    params.updateMainFilters(storeMainFilterSettings);
  }

  restoreSorting(params.pageType, params.pageName, pagesConfig, viewState.sorting);
};

const getValueByCustomPeriodType = <T>(isCustomWeeksSelected: boolean, customWeekValue: T, customMonthValue: T): T => {
  if (isCustomWeeksSelected) return customWeekValue;
  return customMonthValue;
};

const restoreSorting = (pageType: PageType, pageName: PageName, pagesConfig: any, sorting?: SortingOptions) => {
  if (sorting) {
    if (pageType === PageType.OPPORTUNITY_REVIEW && pageName === PageName.OPPORTUNITY_REVIEW) {
      pagesConfig.setSortingOptionsOpportunityReview(sorting);
    } else {
      pagesConfig.setSortingOptions(sorting);
    }
  }
};

// construct object with the general states
export const constructGeneralViewStateProps = (
  savedViewProps:
    | PARestoreLocalSavedViewProps
    | CLRestoreLocalSavedViewProps
    | ProductTabRestoreLocalSavedViewProps
    | OpportunityReviewRestoreLocalSavedViewProps
    | ExploreTabRestoreLocalSavedViewProps
    | RetailerOverviewRestoreLocalSavedViewProps
    | TradingAssistantRestoreLocalSavedViewProps,
) => {
  const {
    productType,
    setIsSubscribed,
    setTimeFrame,
    setPeriod,
    setFilters,
    setSelectedCountries,
    setAppliedCountries,
    setDefaultFilters,
    setSelectedProductGroupOptions,
    setAppliedProductGroupOptions,
    setRelativePeriod,
    setGrowthOption,
  } = savedViewProps;

  const generalSavedViewProps: RestoreLocalSavedViewProps = {
    productType,
    setIsSubscribed,
    setTimeFrame,
    setPeriod,
    setFilters,
    setSelectedCountries,
    setAppliedCountries,
    setDefaultFilters,
    setSelectedProductGroupOptions,
    setAppliedProductGroupOptions,
    setRelativePeriod,
    setGrowthOption,
  };

  return generalSavedViewProps;
};

// to check if its a multibanner scenario, and to restore states accordingly if it is
export const isMultibannerChanged = (
  currentClientId: number,
  retailerClientStateClientId: number,
  restoreMultibannerRetailerViewProps: RestoreMultibannerRetailerViewProps,
  restoreProductMultibannerRetailerViewProps: RestoreProductMultibannerRetailerViewProps,
  restoreMultibannerRetailerClient: (props: RestoreMultibannerRetailerViewProps) => void,
  restoreProductMultibannerRetailerClient: (props: RestoreProductMultibannerRetailerViewProps) => void,
): void => {
  if (!isEqual(currentClientId, retailerClientStateClientId)) {
    restoreMultibannerRetailerClient(restoreMultibannerRetailerViewProps);
    restoreProductMultibannerRetailerClient(restoreProductMultibannerRetailerViewProps);
  }
};

/**
 * Updates the Redux value for a custom period (months/weeks) if the setter function is provided.
 * @param periodState - The current Redux state for the period (months/weeks).
 * @param setPeriodState - Setter function for updating the Redux state.
 */
const setCustomPeriod = <T>(periodState: T, setPeriodState?: (state: T) => void) => {
  setPeriodState?.(periodState);
};

// to get handled time frame only for MAT and YTD
export const getHandledTimeFrame: (periodicity: Periodicity, matYtdTimePeriodLabel?: string) => Periodicity = (
  periodicity,
  matYtdTimePeriodLabel,
) => {
  if (isPeriodicityMATOrYTD(periodicity) && matYtdTimePeriodLabel) {
    return matYtdTimePeriodLabel.includes('Week') ? Periodicity.Weekly : Periodicity.Monthly;
  } else {
    return periodicity;
  }
};

export const getLatestPeriod: (
  initialFilter: InitialFilter[],
  periodicity: Periodicity,
  latestPeriod: BaseTimePeriod,
  matYtdLabel?: string,
) => BaseTimePeriod = (initialFilter, periodicity, latestPeriod, matYtdLabel) => {
  initialFilter.forEach((filter) => {
    let currentPeriod = getRestoreSaveViewLatestPeriod(periodicity, filter.timePeriod, matYtdLabel);

    if (currentPeriod && !isEmpty(currentPeriod.label)) {
      latestPeriod = currentPeriod;
    }
  });

  return latestPeriod;
};

export const handleCustomPeriodicityAndRestoreGeneralViewState: (
  viewState: OpportunityReviewViewState | ExploreViewState | TradingAssistantViewState,
  params: ConstructGeneralViewStateParam,
  setCustomMonthsPeriodState?: (customMonthsPeriodState: CustomMonthsPeriodState) => void,
  setCustomWeeksPeriodState?: (customWeeksPeriodState: CustomWeeksPeriodState) => void,
) => void = (viewState, params, setCustomMonthsPeriodState, setCustomWeeksPeriodState) => {
  if (viewState.mainFilter && Periodicity.CustomMonths === viewState.periodicity && setCustomMonthsPeriodState) {
    setCustomMonthsPeriodState(viewState.mainFilter.customMonthsPeriodState);
    restoreGeneralViewState(params);
  } else if (viewState.mainFilter && Periodicity.CustomWeeks === viewState.periodicity && setCustomWeeksPeriodState) {
    setCustomWeeksPeriodState(viewState.mainFilter.customWeeksPeriodState);
    restoreGeneralViewState(params);
  } else {
    restoreGeneralViewState(params, setCustomMonthsPeriodState, setCustomWeeksPeriodState);
  }
};

type HandleRestoreMultibannerChangeParams = {
  viewState: OpportunityReviewViewState | ExploreViewState;
  restoreMultibannerRetailerClient: (props: RestoreMultibannerRetailerViewProps) => void;
  restoreProductMultibannerRetailerClient: (props: RestoreProductMultibannerRetailerViewProps) => void;
} & Omit<RestoreMultibannerRetailerViewProps, 'initialFilterState' | 'appliedCountry'>;

export function handleRestoreMultibannerChange({
  viewState,
  retailerClientState,
  pageName,
  pageType,
  pagesConfig,
  setFilters,
  setAppliedCountries,
  setSelectedCountries,
  setDefaultFilters,
  restoreMultibannerRetailerClient,
  restoreProductMultibannerRetailerClient,
}: HandleRestoreMultibannerChangeParams) {
  const restoreMultibannerProps: RestoreMultibannerRetailerViewProps = {
    pagesConfig,
    retailerClientState: viewState.retailerClientState,
    initialFilterState: viewState.initialFilterState,
    appliedCountry: viewState.appliedCountries,
    pageName,
    pageType,
    setFilters,
    setAppliedCountries,
    setSelectedCountries,
    setDefaultFilters,
  };
  const restoreProductMultibannerProps = {
    pagesConfig: pagesConfig,
    hitlistRetailerClientState: viewState.hitlistRetailerClientState,
    hitllistInitialFilterState: viewState.hitlistInitialFilterState,
    productMainFilterState: viewState.productMainFilterState,
    appliedCountry: viewState.appliedCountries,
    pageName: pageName,
  };

  isMultibannerChanged(
    viewState.retailerClientState.clientId,
    retailerClientState.clientId,
    restoreMultibannerProps,
    restoreProductMultibannerProps,
    restoreMultibannerRetailerClient,
    restoreProductMultibannerRetailerClient,
  );
}
